import type { Channel } from '@shared/realtime';
import { type UseRealtimeConnectionStatus, useRealtime } from 'inngest/react';
import type { Realtime } from 'inngest/realtime';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

export interface RealtimeMessage<TData = Record<string, unknown>> {
  channel: string;
  topic: string;
  data: TData;
  timestamp: string;
}

export type RealtimeCallback<TChannel extends Channel<any, any>, TTopic extends keyof TChannel['schemas']> = (
  message: RealtimeMessage<z.infer<TChannel['schemas'][TTopic]>>
) => void;

export interface RealtimeListener<TChannel extends Channel<any, any>> {
  subscribe: <K extends keyof TChannel['schemas']>(topic: K, callback: RealtimeCallback<TChannel, K>) => () => void;
  unsubscribe: (topic: keyof TChannel['schemas']) => void;
  state: UseRealtimeConnectionStatus;
}

interface UseInngestRealtimeOptions {
  enabled?: boolean;
  getSubscriptionToken: () => Promise<Realtime.Subscribe.Token>;
  onStateChange?: (state: UseRealtimeConnectionStatus) => void;
}

/**
 * The realtime WebSocket base URL for the local Inngest dev server.
 *
 * v4's `useRealtime` (inngest/react) resolves the connect endpoint from
 * `apiBaseUrl ?? token.apiBaseUrl`, defaulting to `api.inngest.com` when both are
 * absent. Our `getRealtimeToken` GraphQL type exposes only `key`/`channel`/`topics`,
 * so the token reaching the browser carries no `apiBaseUrl` — in local dev the
 * subscription would point at Inngest Cloud, get rejected, and refetch the token in
 * an endless loop. `INNGEST_DEV` is the dev server URL (browser-exposed via
 * redwood.toml, unset in production), so pass it through as `apiBaseUrl` to pin the
 * WebSocket at the local dev server. Undefined in production → cloud default.
 */
const parseDevRealtimeBaseUrl = (): string | undefined => {
  const devValue = process.env.INNGEST_DEV;
  if (!devValue) return undefined;
  try {
    return new URL(devValue).href;
  } catch {
    // INNGEST_DEV can be a boolean-ish flag ("1"/"true") rather than a URL — ignore.
    return undefined;
  }
};

export const useInngestRealtime = <TChannel extends Channel<any, any>>(
  options: UseInngestRealtimeOptions
): RealtimeListener<TChannel> => {
  const { getSubscriptionToken, enabled, onStateChange } = options;

  const [callbacks, setCallbacks] = useState<
    Map<keyof TChannel['schemas'], Set<RealtimeCallback<TChannel, keyof TChannel['schemas']>>>
  >(new Map());

  const apiBaseUrl = useMemo(() => parseDevRealtimeBaseUrl(), []);

  const { connectionStatus, messages } = useRealtime({
    token: getSubscriptionToken,
    enabled: enabled ?? true,
    apiBaseUrl,
  });

  const message = messages.last;

  useEffect(() => {
    onStateChange?.(connectionStatus);
  }, [connectionStatus, onStateChange]);

  useEffect(() => {
    if (!message) return;

    // Handle single message (messages.last is the latest message)
    const realtimeMessage: RealtimeMessage = {
      channel: message.channel ?? '',
      topic: (message as any).topic ?? '',
      data: message.data || {},
      timestamp: new Date().toISOString(),
    };

    // Use topic as the key for callbacks
    const topicCallbacks = callbacks.get(realtimeMessage.topic);
    if (topicCallbacks) {
      topicCallbacks.forEach((cb) => {
        try {
          cb(realtimeMessage);
        } catch (error) {
          console.error('Error in realtime callback:', error);
        }
      });
    }
  }, [message, callbacks]);

  const subscribe = useCallback(
    <K extends keyof TChannel['schemas']>(topic: K, callback: RealtimeCallback<TChannel, K>) => {
      setCallbacks((prev) => {
        const newMap = new Map(prev);
        if (!newMap.has(topic)) {
          newMap.set(topic, new Set());
        }
        newMap.get(topic)?.add(callback as RealtimeCallback<TChannel, keyof TChannel['schemas']>);
        return newMap;
      });

      return () => {
        setCallbacks((prev) => {
          const newMap = new Map(prev);
          const topicCallbacks = newMap.get(topic);
          if (topicCallbacks) {
            topicCallbacks.delete(callback as RealtimeCallback<TChannel, keyof TChannel['schemas']>);
            if (topicCallbacks.size === 0) {
              newMap.delete(topic);
            }
          }
          return newMap;
        });
      };
    },
    []
  );

  const unsubscribe = useCallback((topic: keyof TChannel['schemas']) => {
    setCallbacks((prev) => {
      const newMap = new Map(prev);
      newMap.delete(topic);
      return newMap;
    });
  }, []);

  return useMemo(
    () => ({ subscribe, unsubscribe, state: connectionStatus }),
    [subscribe, unsubscribe, connectionStatus]
  );
};
